/**
 * Cálculo puro de totales de cobro (sin store ni RN). Usado por la UI y tests en Node.
 */
import {
  DEFAULT_MONEY_SCALE,
  roundMoney,
  toMinorUnits,
  type MoneyScale,
} from '@shared/money';
import { parseDecimalInput } from '../../shared/numeric/localeNumeric';

export type PosCheckoutLineTotalInput = {
  price: number;
  quantity: number;
  /** Precio unitario efectivo tras promos; default = `price`. */
  effectiveUnitPrice?: number;
  lineDiscountTotal?: number;
};

export type PosCheckoutPaymentAmountInput = { amount: number | string };

export function toPaymentAmountNumber(
  amount: number | string,
  scale: MoneyScale = DEFAULT_MONEY_SCALE,
): number {
  const raw =
    typeof amount === 'number'
      ? Number.isFinite(amount)
        ? amount
        : 0
      : (parseDecimalInput(amount, { min: 0, moneyScale: scale }) ?? 0);
  return roundMoney(raw, scale);
}

export type PosCheckoutTotalsResult = {
  subtotal: number;
  discountTotal: number;
  total: number;
  totalIngresado: number;
  vuelto: number;
  saldoPendiente: number;
};

export function computePosCheckoutTotals(
  lines: PosCheckoutLineTotalInput[],
  paymentDrafts: PosCheckoutPaymentAmountInput[],
  scale: MoneyScale = DEFAULT_MONEY_SCALE,
): PosCheckoutTotalsResult {
  const { subtotal, total, discountTotal } = lines.reduce(
    (acc, item) => {
      const unitOriginal = item.price;
      const unitEffective = item.effectiveUnitPrice ?? unitOriginal;
      const lineTotal = unitOriginal * item.quantity;
      const lineEffective = unitEffective * item.quantity;
      acc.subtotal += lineTotal;
      acc.total += lineEffective;
      acc.discountTotal += item.lineDiscountTotal ?? Math.max(0, lineTotal - lineEffective);
      return acc;
    },
    { subtotal: 0, total: 0, discountTotal: 0 },
  );
  const totalIngresado = paymentDrafts.reduce(
    (sum, draft) => sum + toPaymentAmountNumber(draft.amount, scale),
    0,
  );
  const subtotalR = roundMoney(subtotal, scale);
  const discountR = roundMoney(Math.max(0, discountTotal), scale);
  const totalR = roundMoney(total, scale);
  const ingresadoR = roundMoney(totalIngresado, scale);
  const vueltoMinor = Math.max(0, toMinorUnits(ingresadoR, scale) - toMinorUnits(totalR, scale));
  const saldoMinor = Math.max(0, toMinorUnits(totalR, scale) - toMinorUnits(ingresadoR, scale));
  const factor = 10 ** scale.fractionDigits;
  return {
    subtotal: subtotalR,
    discountTotal: discountR,
    total: totalR,
    totalIngresado: ingresadoR,
    vuelto: vueltoMinor / factor,
    saldoPendiente: saldoMinor / factor,
  };
}
